import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useError } from '../contexts/ErrorContext';
import AppLayout from '../components/AppLayout';
import LoadingSkeleton from '../components/LoadingSkeleton';
import EmptyState from '../components/EmptyState';
import QRCodeScanner from '../components/QRCodeScanner';
import SupporterBadge from '../components/SupporterBadge';
import { extractErrorMessage } from '../utils/errorHandling';
import {
  connectToFriend,
  downloadWireGuardConfig,
  wireGuardConfigFileName,
} from '../utils/connectToFriend';
import {
  friendsApi,
  FriendSummary,
  FriendRequestsResponse,
  FriendRequestEntry,
  QRCodeResponse,
  AcceptFriendRequestWithLimits,
  UpdateFriendshipLimits
} from '../api/friends';
import FriendRequestApprovalModal from '../components/FriendRequestApprovalModal';
import FriendLimitSettingsModal from '../components/FriendLimitSettingsModal';
import RemoveFriendConfirmationModal from '../components/RemoveFriendConfirmationModal';
import UsageHistoryModal from '../components/UsageHistoryModal';
import {
  UserGroupIcon,
  UserPlusIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  XMarkIcon,
  QrCodeIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';

const Friends: React.FC = () => {
  const toast = useToast();
  const { addError } = useError();
  const [friends, setFriends] = useState<FriendSummary[]>([]);
  // friendship_id of the friend currently being connected to (#782)
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [friendRequests, setFriendRequests] = useState<FriendRequestsResponse>({ sent: [], received: [] });
  const [qrCodeData, setQrCodeData] = useState<QRCodeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Add friend form
  const [friendCode, setFriendCode] = useState('');
  const [isAddingFriend, setIsAddingFriend] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);

  // Friend request approval modal
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<FriendRequestEntry | null>(null);

  // Friend limit settings modal
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<FriendSummary | null>(null);

  // Remove friend confirmation modal
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState<FriendSummary | null>(null);

  // Usage history modal
  const [usageModalOpen, setUsageModalOpen] = useState(false);

  // Dropdown menu state (track which friend's menu is open)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Filtered friends based on search
  const filteredFriends = friends.filter(friend =>
    friend.friend.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (friend.friend.username && friend.friend.username.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [friendsResponse, requestsResponse, qrResponse] = await Promise.all([
        friendsApi.getFriends(),
        friendsApi.getFriendRequests(),
        friendsApi.getQRCode()
      ]);

      setFriends(friendsResponse.data);
      setFriendRequests(requestsResponse.data);
      setQrCodeData(qrResponse.data);
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to fetch friends data');
      addError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [addError]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const addFriend = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!friendCode.trim()) {
      toast.error('Please enter a friend code');
      return;
    }

    try {
      setIsAddingFriend(true);
      await friendsApi.addFriendByCode(friendCode.trim());
      toast.success('Friend request sent successfully');
      setFriendCode('');
      await fetchAllData();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to send friend request');
      addError(apiError.message);
    } finally {
      setIsAddingFriend(false);
    }
  };

  const acceptFriendRequest = async (requestId: string, limits?: AcceptFriendRequestWithLimits) => {
    try {
      await friendsApi.acceptFriendRequest(requestId, limits);
      toast.success('You are now friends! 🤝');
      await fetchAllData();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to accept friend request');
      addError(apiError.message);
    }
  };

  const openApprovalModal = (request: FriendRequestEntry) => {
    setSelectedRequest(request);
    setApprovalModalOpen(true);
  };

  const closeApprovalModal = () => {
    setSelectedRequest(null);
    setApprovalModalOpen(false);
  };

  const openSettingsModal = (friend: FriendSummary) => {
    setSelectedFriend(friend);
    setSettingsModalOpen(true);
  };

  const closeSettingsModal = () => {
    setSelectedFriend(null);
    setSettingsModalOpen(false);
  };

  const updateFriendLimits = async (friendshipId: string, limits: UpdateFriendshipLimits) => {
    try {
      await friendsApi.updateFriendLimits(friendshipId, limits);
      toast.success('Connection limits updated successfully');
      await fetchAllData();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to update connection limits');
      addError(apiError.message);
    }
  };

  const declineFriendRequest = async (requestId: string) => {
    try {
      await friendsApi.declineFriendRequest(requestId);
      toast.success('Friend request cancelled');
      await fetchAllData();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to cancel friend request');
      addError(apiError.message);
    }
  };

  const removeFriend = async (friendshipId: string) => {
    try {
      await friendsApi.removeFriend(friendshipId);
      toast.success('Friend removed successfully');
      await fetchAllData();
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to remove friend');
      addError(apiError.message);
    }
  };

  // Menu handlers
  const toggleMenu = (friendshipId: string) => {
    setOpenMenuId(openMenuId === friendshipId ? null : friendshipId);
  };

  const openRemoveModal = (friend: FriendSummary) => {
    setFriendToRemove(friend);
    setRemoveModalOpen(true);
    setOpenMenuId(null); // Close menu
  };

  const closeRemoveModal = () => {
    setFriendToRemove(null);
    setRemoveModalOpen(false);
  };

  const openUsageModal = () => {
    setUsageModalOpen(true);
    setOpenMenuId(null); // Close menu
  };

  const closeUsageModal = () => {
    setUsageModalOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard`);
  };

  // One-tap connect to a friend directly from their row: request a session and
  // download the WireGuard config to import — no manually-typed IDs. (#782)
  const handleConnect = async (friend: FriendSummary) => {
    if (connectingId) return;
    setConnectingId(friend.friendship_id);
    try {
      const result = await connectToFriend(friend.friend.id);
      downloadWireGuardConfig(result.config, wireGuardConfigFileName(result.hostName));
      toast.success(
        `Connected to ${result.hostName}. Config downloaded — import it into WireGuard to start the tunnel.`
      );
    } catch (error) {
      toast.error(
        extractErrorMessage(error, `Failed to connect to ${friend.friend.display_name}`).message
      );
    } finally {
      setConnectingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <LoadingSkeleton type="card" count={3} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Add Friend & QR Code */}
          <div className="lg:col-span-1">
            {/* Add Friend */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <UserPlusIcon className="h-6 w-6 text-gray-400 mr-3" />
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Add Friend
                  </h3>
                </div>

                <form onSubmit={addFriend} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Friend Code
                    </label>
                    <input
                      type="text"
                      value={friendCode}
                      onChange={(e) => setFriendCode(e.target.value)}
                      placeholder="Enter friend's code"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="submit"
                      disabled={isAddingFriend}
                      className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      {isAddingFriend ? 'Sending...' : 'Send Request'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowQRScanner(true)}
                      className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <QrCodeIcon className="h-5 w-5 mr-2" />
                      Scan QR
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Your Friend Code */}
            {qrCodeData && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center mb-4">
                    <QrCodeIcon className="h-6 w-6 text-gray-400 mr-3" />
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Your Friend Code
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Friend Code
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          readOnly
                          value={qrCodeData.friend_code}
                          className="flex-1 block border-gray-300 rounded-l-md shadow-sm bg-gray-50 sm:text-sm"
                        />
                        <button
                          onClick={() => copyToClipboard(qrCodeData.friend_code, 'Friend code')}
                          className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 hover:text-gray-700"
                        >
                          <ClipboardDocumentIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        QR Code Link
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          readOnly
                          value={qrCodeData.qr_code_data}
                          className="flex-1 block border-gray-300 rounded-l-md shadow-sm bg-gray-50 sm:text-sm text-xs"
                        />
                        <button
                          onClick={() => copyToClipboard(qrCodeData.qr_code_data, 'QR code link')}
                          className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 hover:text-gray-700"
                        >
                          <ClipboardDocumentIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <p className="text-sm text-gray-500">
                      Share your friend code or QR link with friends to let them add you
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Friends List & Requests */}
          <div className="lg:col-span-2">
            {/* Friend Requests */}
            {(friendRequests.received.length > 0 || friendRequests.sent.length > 0) && (
              <div className="bg-white shadow rounded-lg mb-6">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Friend Requests
                  </h3>

                  {/* Received Requests */}
                  {friendRequests.received.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-700 mb-3">
                        Received ({friendRequests.received.length})
                      </h4>
                      <div className="space-y-3">
                        {friendRequests.received.map((request) => (
                          <div key={request.request_id} className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                  <UserGroupIcon className="h-6 w-6 text-yellow-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {request.friend.display_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {request.friend.username && `@${request.friend.username} • `}
                                  Sent {formatDate(request.created_at)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openApprovalModal(request)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 min-h-[44px]"
                              >
                                <CheckIcon className="h-4 w-4 mr-1" />
                                Review
                              </button>
                              <button
                                onClick={() => declineFriendRequest(request.request_id)}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 min-h-[44px]"
                              >
                                <XMarkIcon className="h-4 w-4 mr-1" />
                                Decline
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sent Requests */}
                  {friendRequests.sent.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-700 mb-3">
                        Sent ({friendRequests.sent.length})
                      </h4>
                      <div className="space-y-3">
                        {friendRequests.sent.map((request) => (
                          <div key={request.request_id} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <UserGroupIcon className="h-6 w-6 text-primary-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {request.friend.display_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {request.friend.username && `@${request.friend.username} • `}
                                  Sent {formatDate(request.created_at)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-primary-600 font-medium">
                                Pending
                              </span>
                              <button
                                onClick={() => declineFriendRequest(request.request_id)}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 min-h-[44px]"
                              >
                                <XMarkIcon className="h-4 w-4 mr-1" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Friends List */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    My Friends ({friends.length})
                  </h3>
                </div>

                {/* Search Bar */}
                {friends.length > 0 && (
                  <div className="mb-4">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Search friends..."
                      />
                    </div>
                  </div>
                )}

                {friends.length === 0 ? (
                  <EmptyState
                    icon={UserGroupIcon}
                    title="No friends yet"
                    description="Start by adding friends using their friend code."
                  />
                ) : filteredFriends.length === 0 ? (
                  <EmptyState
                    icon={MagnifyingGlassIcon}
                    title="No friends found"
                    description="Try a different search term."
                  />
                ) : (
                  <div className="space-y-4">
                    {filteredFriends.map((friend) => (
                      <div key={friend.friendship_id} className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              friend.friend.sharing_enabled ? 'bg-green-100' : 'bg-gray-100'
                            }`}>
                              <UserGroupIcon className={`h-6 w-6 ${
                                friend.friend.sharing_enabled ? 'text-green-600' : 'text-gray-400'
                              }`} />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <div className="text-sm font-medium text-gray-900">
                                {friend.friend.display_name}
                              </div>
                              <div className="ml-2 flex items-center gap-2">
                                {friend.friend.is_online ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                                    Online
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-1.5"></span>
                                    Offline
                                  </span>
                                )}
                                {friend.friend.sharing_enabled && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-primary-800">
                                    Sharing
                                  </span>
                                )}
                                {friend.friend.is_supporter && <SupporterBadge />}
                              </div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {friend.friend.username && `@${friend.friend.username}`}
                              {friend.friend.location && ` • ${friend.friend.location}`}
                              {friend.accepted_at && ` • Friends since ${formatDate(friend.accepted_at)}`}
                              {!friend.friend.is_online && friend.friend.last_seen && ` • Last seen ${formatDate(friend.friend.last_seen)}`}
                            </div>
                            {(friend.bandwidth_limit_mbps || friend.daily_data_limit_gb) && (
                              <div className="text-xs text-gray-400 mt-1">
                                {friend.bandwidth_limit_mbps && `${friend.bandwidth_limit_mbps} Mbps limit`}
                                {friend.bandwidth_limit_mbps && friend.daily_data_limit_gb && ' • '}
                                {friend.daily_data_limit_gb && `${friend.daily_data_limit_gb} GB daily limit`}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {friend.friend.sharing_enabled && (
                            <button
                              onClick={() => handleConnect(friend)}
                              disabled={!!connectingId}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200 min-h-[44px] disabled:opacity-50"
                            >
                              {connectingId === friend.friendship_id ? 'Connecting…' : 'Connect'}
                            </button>
                          )}
                          <div className="relative">
                            <button
                              onClick={() => toggleMenu(friend.friendship_id)}
                              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 min-h-[44px] min-w-[44px]"
                              aria-label="Friend options"
                            >
                              <EllipsisVerticalIcon className="h-5 w-5" />
                            </button>

                            {/* Dropdown Menu */}
                            {openMenuId === friend.friendship_id && (
                              <div
                                ref={menuRef}
                                className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                              >
                                <div className="py-1" role="menu" aria-orientation="vertical">
                                  <button
                                    onClick={openUsageModal}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    role="menuitem"
                                  >
                                    View Usage
                                  </button>
                                  <button
                                    onClick={() => {
                                      openSettingsModal(friend);
                                      setOpenMenuId(null);
                                    }}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    role="menuitem"
                                  >
                                    Edit Limits
                                  </button>
                                  <div className="border-t border-gray-100"></div>
                                  <button
                                    onClick={() => openRemoveModal(friend)}
                                    className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                    role="menuitem"
                                  >
                                    Remove Friend
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Scanner Modal */}
      {showQRScanner && (
        <QRCodeScanner
          onScan={(code) => {
            setFriendCode(code);
            setShowQRScanner(false);
            toast.info('QR code scanned! Review and submit to add friend.');
          }}
          onClose={() => setShowQRScanner(false)}
        />
      )}

      {/* Friend Request Approval Modal */}
      <FriendRequestApprovalModal
        request={selectedRequest}
        isOpen={approvalModalOpen}
        onClose={closeApprovalModal}
        onAccept={acceptFriendRequest}
        onDecline={declineFriendRequest}
      />

      {/* Friend Limit Settings Modal */}
      <FriendLimitSettingsModal
        friend={selectedFriend}
        isOpen={settingsModalOpen}
        onClose={closeSettingsModal}
        onUpdate={updateFriendLimits}
      />

      {/* Remove Friend Confirmation Modal */}
      <RemoveFriendConfirmationModal
        friend={friendToRemove}
        isOpen={removeModalOpen}
        onClose={closeRemoveModal}
        onConfirm={removeFriend}
      />

      {/* Usage History Modal */}
      <UsageHistoryModal
        isOpen={usageModalOpen}
        onClose={closeUsageModal}
      />
    </AppLayout>
  );
};

export default Friends;
